import { FrontendApi, Configuration as OryConfiguration } from '@ory/client';
import { HttpClient, provideHttpClient } from '@angular/common/http';
import { ErrorHandler, NgModule, provideZoneChangeDetection } from '@angular/core';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideRemoteConfig, getRemoteConfig } from '@angular/fire/remote-config';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { RollbarModule, RollbarService } from 'angular-rollbar';
import { MarkdownModule, MARKED_OPTIONS, MarkedOptions } from 'ngx-markdown';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CaDesignModule } from './ca-design/ca-design.module';
import { CustomSerializer } from './custom-serializer';
import { ERROR_LOGGING_SERVICE, NFErrorHandler } from './error-handler/error-handler';
import { NotFoundModule } from './not-found/not-found.module';
import { GOOGLE_MAPS_API_KEY } from './shared/services/index';
import { SharedModule } from './shared/shared.module';
import { EFFECTS } from './store/effects/index';
import { META_REDUCERS, REDUCERS } from './store/reducers/index';
import {
  ApiModule as NestApiModule,
  Configuration,
  ConfigurationParameters,
  BASE_PATH,
} from '@careapp-group/ng-sdk';
import { QuillConfigModule } from 'ngx-quill/config';

import {
  ApiModule as NestNewApiModule,
  BASE_PATH as NEST_NEW_BASE_PATH,
  Configuration as NestNewConfiguration,
} from '@careapp-group/ng-sdk';

import {
  StreamChatModule,
  StreamAutocompleteTextareaModule,
  ChannelService,
  ChatClientService,
  StreamI18nService,
} from 'stream-chat-angular';

import { provideLottieOptions } from 'ngx-lottie';
import player from 'lottie-web';

import { Ability, PureAbility } from '@casl/ability';
import { CA_ENVIRONMENT, WINDOW } from './app.di';
import Quill from 'quill';
import { QuillModule } from 'ngx-quill';
// this import is required for the JS to be available at runtime
import '@windmillcode/quill-emoji';
import { AblePipe } from '@casl/angular';
import { FILE_DOWNLOAD_PIPE_DOWNLOAD_MODE } from './shared/pipes';

var { version } = require('../environments/version.json');

export function windowFactory() {
  return window;
}

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, undefined, version ? `.json?v=${version}` : undefined);
}

export function providers() {
  return [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideRemoteConfig(() => getRemoteConfig()),
    provideAnalytics(() => getAnalytics()),
    provideMessaging(() => getMessaging()),
    provideLottieOptions({
      player: () => player,
    }),
    provideHttpClient(),
    {
      provide: RouterStateSerializer,
      useClass: CustomSerializer,
    },
    {
      provide: GOOGLE_MAPS_API_KEY,
      useValue: environment.GOOGLE_MAPS_API_KEY,
    },
    {
      provide: ERROR_LOGGING_SERVICE,
      useClass: RollbarService,
    },
    {
      provide: FrontendApi,
      useValue: new FrontendApi(
        new OryConfiguration({
          basePath: environment.kratosFrontendUrl,
          baseOptions: {
            withCredentials: true,
          },
        }),
      ),
    },
    {
      provide: WINDOW,
      useFactory: windowFactory,
    },
    {
      provide: CA_ENVIRONMENT,
      useValue: environment,
    },
    { provide: BASE_PATH, useValue: environment.API_V2_BASE_PATH },
    { provide: NEST_NEW_BASE_PATH, useValue: environment.API_V2_BASE_PATH },
    // Required for conversations with Stream IO.
    ChatClientService,
    ChannelService,
    StreamI18nService,
    { provide: Ability, useValue: new Ability() },
    { provide: PureAbility, useExisting: Ability },
    { provide: FILE_DOWNLOAD_PIPE_DOWNLOAD_MODE, useValue: environment.fileDownloadMode },
    { provide: WINDOW, useValue: window },
    provideZoneChangeDetection(),
  ];
}

export function productionProviders() {
  return [
    ...providers(),
    {
      provide: ErrorHandler,
      useClass: NFErrorHandler,
    },
  ];
}

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    withCredentials: true,
  };
  return new Configuration(params);
}

export function newApiConfigFactory(): NestNewConfiguration {
  return new NestNewConfiguration({
    withCredentials: true,
  });
}

@NgModule({
  imports: [RouterModule],
  declarations: [AppComponent],
  exports: [AppComponent],
  providers: environment.production ? productionProviders() : providers(),
})
export class AppComponentModule {}

// Modules that are supported by all the browsers we support
export const moduleImportsForAllBrowsers: any[] = [
  AblePipe,
  NestApiModule.forRoot(apiConfigFactory),
  NestNewApiModule.forRoot(newApiConfigFactory),
  AppComponentModule,
  AppRoutingModule,
  BrowserModule,
  BrowserAnimationsModule,
  CaDesignModule,
  MarkdownModule.forRoot({
    markedOptions: {
      provide: MARKED_OPTIONS,
      useValue: {},
    },
  }),
  QuillModule.forRoot(),
  SharedModule.forRoot(),
  StoreModule.forRoot(REDUCERS, { metaReducers: META_REDUCERS }),
  EffectsModule.forRoot(EFFECTS),
  RollbarModule.forRoot({
    accessToken: environment.ROLLBAR_ACCESS_TOKEN,
    payload: {
      environment: environment.ROLLBAR_ENV_NAME,
    },
    captureUnhandledRejections: true,
    captureUncaught: true,
  }),
  RouterModule,
  StoreDevtoolsModule.instrument({
    logOnly: environment.production,
    maxAge: environment.production ? 25 : undefined,
    connectInZone: true,
  }),
  StoreRouterConnectingModule.forRoot(),
  TranslateModule.forRoot({
    defaultLanguage: 'en',
    loader: {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpClient],
    },
  }),
  // Required for Stream conversations
  StreamAutocompleteTextareaModule,
  StreamChatModule,
  // Must be last - route catch-all to redirect to /
  NotFoundModule,
];

/**
 * Returns the list of module imports required for the app
 *
 * This is primarily to work around browser compatibility issues
 * in AngularFire
 */
@NgModule({
  imports: [
    // Add Firebase Analytics if we aren't using Internet Explorer
    // Workaround for CA-434 and this upstream bug:
    //
    // https://github.com/angular/angularfire/issues/2296
    ...moduleImportsForAllBrowsers,

    QuillConfigModule.forRoot({
      theme: null,
      bounds: 'quill-editor',
      modules: {
        'emoji-toolbar': true,
        'emoji-shortname': true,
        toolbar: [
          [
            'bold',
            'italic',
            'underline',
            'clean',
            { header: 1 },
            { header: 2 },
            { list: 'ordered' },
            { list: 'bullet' },
            'emoji',
            'link',
          ],
        ],
      },
    }),
  ],
  providers: environment.production ? productionProviders() : providers(),
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    iconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
  ) {
    iconRegistry.setDefaultFontSetClass('material-icons-outlined');
    iconRegistry.addSvgIcon(
      'ca-family',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '/assets/svg/maticon_custom_family_24px.svg',
      ),
    );
    iconRegistry.addSvgIcon(
      'ca-gift',
      this.domSanitizer.bypassSecurityTrustResourceUrl('/assets/svg/careapp-gift-icon.svg'),
    );
    iconRegistry.addSvgIcon(
      'ca-activity-library',
      this.domSanitizer.bypassSecurityTrustResourceUrl(
        '/assets/svg/maticon_custom_mindfulness_24px.svg',
      ),
    );
  }
}
